import { getCurrentTenant, getLoggedInUserInfo } from './../../../state/index';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ILocalizationRecord } from '../shared/models/localization-record.model';
import { LocalizationService } from '../shared/services/localization.service';
import { Subscription } from 'rxjs';
import { Language } from '../shared/models/language.model';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { startWith, take, takeWhile } from 'rxjs/operators';
import { Router } from '@angular/router';
import { IStaticLocalizedText } from '../shared/models/static-localized-text.model';
import { ILocalizationUpdateRequest } from '../shared/models/localization-update-request.model';
import { IStaticLocalizedTextUpdate } from '../shared/models/static-localized-text-update.model';
import { DirtyFormChecker } from 'src/app/shared/routeguards/DirtyFormChecker';
import { ToastService } from 'src/app/shared/services/toast.service';
import { Store } from '@ngrx/store';
import { State } from 'src/app/state/app.state';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-admin-localization-settings',
    templateUrl: './localization-settings.component.html',
    styleUrls: ['./localization-settings.component.scss']
})
export class LocalizationSettingsComponent implements OnInit, OnDestroy, DirtyFormChecker {

    public formGroup: FormGroup = new FormGroup({
        languages: new FormControl(),
        editField: new FormControl('', [Validators.required])
    });
    public item: IStaticLocalizedText[] = [];
    public localizationRecordToEdit: ILocalizationRecord | null = null;

    public languages: Language[] = [];
    public selectedLanguageString: string = '';
    private subscriptions = new Subscription();
    private languageSelectionOverride: Language | null = null;
    private tenantId: number | undefined;
    public englishText: string;
    public spanishText: string;
    public permissionGroup: string = '';
    mounted:boolean = true;

    constructor(private localizationService: LocalizationService,
                private store: Store<State>,
                private toastService: ToastService,
                private router: Router,
                private translateService: TranslateService) {
                this.englishText = this.translateService.instant('_General_English_EN_Text_');
                this.spanishText = this.translateService.instant('_General_Spanish_ES_Text_');
                }
    hasUnsavedData(): boolean {
        return this.isDirty();
    }


    ngOnInit(): void {
        this._loadData();
        this._OnLanguageSelectionChange();
        this._OnEditFieldChange();
        this.store.select(getLoggedInUserInfo)
            .pipe(takeWhile(() => this.mounted))
            .subscribe(ecc => this.permissionGroup = ecc?.permissionGroup ?? "")
    }

    /**
     *   Load the data from service, assign the items for the editable item
     */
    private _loadData() {
        this.localizationRecordToEdit = this.localizationService.getLocalizationRecordToEdit();
        if (!this.localizationRecordToEdit) {
            this._navigateToLocalizationGrid(); // No record selected to edit
        }
        let encodeLocalizeKey = encodeURIComponent(this.localizationRecordToEdit.localizeKey);
        this.localizationService.getLocalizationsForKey(
            encodeLocalizeKey, this.localizationRecordToEdit.applicationModuleId)
            .subscribe(localizationResponse => {
                this.item = Object.assign([], localizationResponse);
                // this.uneditedItemString = JSON.stringify(localizationResponse);
                this.formGroup.markAsPristine();
                this._loadLanguageLookups();
            });

        this.subscriptions.add(this.store.select(getCurrentTenant).subscribe(t => this.tenantId = t.tenantID));
    }

    /**
     *   Loads the Languages from server
     *   Sets the default value for language dropdown
     */
    private _loadLanguageLookups() {
        this.localizationService.getLanguages().subscribe(lans => {
            lans.forEach(lan => {
                if(lan.languageId == 1){
                    lan.languageName = this.englishText;
                }
                else if(lan.languageId == 3){
                    lan.languageName = this.spanishText;
                }
            });
            this.languages = lans;
            this.formGroup.controls.languages.setValue(
                this.languages.find(lan => lan.languageId
                    === (this.languageSelectionOverride?.languageId ?? this.localizationService.getCurrentlySelectedLangauge()!.languageId))
            );
        });
    }

    /**
     *   Updates the edit field with the correct value if the language selection changes
     */
    private _OnLanguageSelectionChange() {
        this.subscriptions.add(this.formGroup.controls.languages.valueChanges.pipe(
            startWith(this.formGroup.controls.languages.value)
        ).subscribe({
            next: (selectedLanguage: Language) => {
                if (this.item) {
                    const localizeValue = this.item.find(stext => stext.languageId === selectedLanguage?.languageId);
                    const editCtrl = this.formGroup.controls.editField;
                    editCtrl.setValue(localizeValue?.textValue, { emitEvent: false });
                    editCtrl.markAsPristine();
                    this.formGroup.markAsPristine();
                }
                this.selectedLanguageString = selectedLanguage?.languageName;
                this.languageSelectionOverride = selectedLanguage;
            }
        }));
    }

    /**
     *   Update the property when edit field changes
     */
    private _OnEditFieldChange() {
        this.subscriptions.add(this.formGroup.controls.editField.valueChanges.subscribe({
            next: (newValue) => {
                if (this.item) {
                    const languageId = this.formGroup.controls.languages.value.languageId;
                    if ((this.item.find(stext => stext.languageId === languageId)) == undefined) {
                        throw Error("StaticLocalizedText record was undefined! Which was not expected!")
                    }
                    this.item.find(stext => stext.languageId === languageId)!.textValue = newValue;
                }
            }
        }));
    }

    /**
     * popup the confirmation if changes are unsaved otherwise redirect to the grid
     */
    private _moveToGridIfChangesAreSaved() {
        this._navigateToLocalizationGrid();
    }

    /**
     *   Returns true if there is any unsaved Changes
     *   Defined a seperate method though 'HasUnSavedData' method is for the same purpose, it depends on the implementation interface
     */
    public isDirty(): boolean {
        // return JSON.stringify(this.item) !== this.uneditedItemString;
        return this.formGroup.dirty;
    }

    public isFormValid(): boolean {
        return this.formGroup.valid;
    }

    /**
     *   Saves all the unsaved changes and refresh the data with new value from the server
     */
    private _save() {
        if (this.localizationRecordToEdit == undefined) {
            throw Error("this.localizationRecordToEdit was undefined! Which was not expected!")
        }
        const request: ILocalizationUpdateRequest = {
            key: encodeURIComponent(this.localizationRecordToEdit.localizeKey),
            moduleId: this.localizationRecordToEdit.applicationModuleId,
            staticLocalizationText: this.item.map(model => this._mapStaticTextModelToUpdateModel(model))
        };
        this.localizationService.updateLocalizations(request).subscribe({
            next: resp => {
                this._loadData();
                this.toastService.showSuccess('_General_Changes_Saved_Successfully_Ts_Text_');
            },
            error: error => {
                this.toastService.showError('_General_Saving_Failed_Ts_Text_');
            }
        });
    }

    /**
     *   From the *IStaticLocalizedText Model create the IStaticLocalizedText UpdateModel for update API
     */
    private _mapStaticTextModelToUpdateModel(model: IStaticLocalizedText): IStaticLocalizedTextUpdate {
        if (this.tenantId == undefined) {
            throw Error("this.tenantId was undefined! Which was not expected!")
        }
        return {
            tenantId: this.tenantId,
            localizationId: model.localizationId,
            languageId: model.languageId,
            textKey: encodeURIComponent(model.textKey),
            textValue: encodeURIComponent(model.textValue)
        };
    }

    // -- Handlers

    public onMoveBackClicked() {
        this._moveToGridIfChangesAreSaved();
    }

    public onSaveClicked() {
        this._save();
    }

    public onCancelClicked() {
        this._moveToGridIfChangesAreSaved();
    }
    // -- Handlers END


    // Navigation
    private _navigateToLocalizationGrid() {
        this.router.navigate(['/admin/localization/grid']);
    }
    // Navigation End

    // FormControl Getters
    get editField() {
        return this.formGroup.get('editField');
    }
    // FormControl Getters END

    public languageTrackBy(index: number, item: Language) {
        return item.languageId;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

  public showSelectedLanguageStringTextIsRequiredError(editField: any): boolean {
        return editField.invalid == true && (editField.dirty == true || editField.touched == true);
    }
}
